import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FiEdit2, FiX, FiCheck, FiClock, FiCalendar, FiClock as FiClockOut } from 'react-icons/fi';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import AlertBox from '../../Components/AlertBox';

const ResponsiveSelect = ({ value, onChange, options, placeholder, name, required, disabled, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target))) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDropdown = () => {
    if (disabled) return;
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
    setIsOpen(!isOpen);
  };

  const selectedOpt = options.find(o => String(o.value) === String(value));
  const displayLabel = selectedOpt ? selectedOpt.label : placeholder;

  const dropdownMenu = isOpen && !disabled ? (
    <div
      ref={dropdownRef}
      className="mcs-dropdown"
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: '4px',
        zIndex: 9999,
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        maxHeight: '250px',
        overflowY: 'auto'
      }}
    >
      {placeholder && (
        <div
          className="mcs-option"
          onClick={() => {
            onChange({ target: { name, value: '' } });
            setIsOpen(false);
          }}
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #f1f5f9',
            backgroundColor: !value ? '#eff6ff' : 'transparent',
            color: !value ? '#1d4ed8' : '#334155',
            fontWeight: !value ? '600' : '400',
            fontSize: '15px',
            cursor: 'pointer',
            textAlign: 'left'
          }}
        >
          {placeholder}
        </div>
      )}
      {options.map(opt => (
        <div
          key={opt.value}
          className="mcs-option"
          onClick={() => {
            onChange({ target: { name, value: opt.value } });
            setIsOpen(false);
          }}
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #f1f5f9',
            backgroundColor: String(value) === String(opt.value) ? '#eff6ff' : 'transparent',
            color: String(value) === String(opt.value) ? '#1d4ed8' : '#334155',
            fontWeight: String(value) === String(opt.value) ? '600' : '400',
            fontSize: '15px',
            cursor: 'pointer',
            wordBreak: 'break-word',
            textAlign: 'left'
          }}
        >
          {opt.label}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <div className="w-full relative" ref={wrapperRef}>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className={`desktop-select w-full ${className || 'border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'}`}
        style={{
          backgroundColor: disabled ? '#f8f9fa' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      <div className="mobile-custom-select" style={{ display: 'none' }}>
        <div
          className={`mcs-trigger w-full bg-white shadow-sm transition-all duration-200 ${className || 'border border-gray-300 rounded-lg p-3 text-sm'}`}
          onClick={openDropdown}
          style={{
            backgroundColor: disabled ? '#f8f9fa' : 'white',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            opacity: disabled ? 0.7 : 1
          }}
        >
          <div className="mcs-label" style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{displayLabel}</div>
          <span style={{ fontSize: '10px', marginLeft: '8px', color: '#6b7280' }}>▼</span>
        </div>
        {dropdownMenu}
      </div>
    </div>
  );
};

// Calculate work hours between punch in and punch out
const calculateWorkHours = (punchIn, punchOut) => {
  if (!punchIn || !punchOut) return '0:00';

  const [inHour, inMinute] = punchIn.split(':').map(Number);
  const [outHour, outMinute] = punchOut.split(':').map(Number);

  const inTotalMinutes = inHour * 60 + inMinute;
  const outTotalMinutes = outHour * 60 + outMinute;

  let diffMinutes = outTotalMinutes - inTotalMinutes;
  if (diffMinutes < 0) diffMinutes += 24 * 60; // Handle next day

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

// Format time to 12-hour format with AM/PM
const formatTo12Hour = (time24) => {
  if (!time24 || time24 === '-' || time24 === '--:--') return time24;

  try {
    const [hours, minutes] = time24.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time24;

    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return time24; // Return original if formatting fails
  }
};

// Convert 12-hour format back to 24-hour format for calculations
const convertTo24Hour = (time12) => {
  if (!time12 || time12 === '-' || time12 === '--:--') return time12;

  try {
    const [time, period] = time12.split(' ');
    if (!time || !period) return time12; // Not in 12-hour format, return as is

    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time12;

    let hour24 = hours;
    if (period.toUpperCase() === 'PM' && hours !== 12) {
      hour24 = hours + 12;
    } else if (period.toUpperCase() === 'AM' && hours === 12) {
      hour24 = 0;
    }

    return `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    return time12; // Return original if conversion fails
  }
};

const calculateLateTime = (punchInTime) => {
  try {
    // Convert 12-hour format to 24-hour format for calculation
    const time24 = convertTo24Hour(punchInTime);
    const [hours, minutes] = time24.split(':').map(Number);

    if (isNaN(hours) || isNaN(minutes)) {
      return { isLate: false, lateTime: '0m' };
    }

    const punchInDate = new Date();
    punchInDate.setHours(hours, minutes, 0, 0);

    const lateThreshold = new Date();
    lateThreshold.setHours(10, 15, 0, 0); // 10:15 AM

    if (punchInDate > lateThreshold) {
      const diffMs = punchInDate - lateThreshold;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const lateHours = Math.floor(diffMinutes / 60);
      const lateMinutes = diffMinutes % 60;

      return {
        isLate: true,
        lateTime: lateHours > 0 ? `${lateHours}h ${lateMinutes}m` : `${lateMinutes}m`
      };
    }

    return { isLate: false, lateTime: '0m' };
  } catch (error) {
    return { isLate: false, lateTime: '0m' };
  }
};

const DailyAttendance = () => {
  const [currentAttendance, setCurrentAttendance] = useState(null);
  const [punchInTime, setPunchInTime] = useState(null);
  const [punchOutTime, setPunchOutTime] = useState(null);
  const [workHours, setWorkHours] = useState('00:00');
  const [attendanceStatus, setAttendanceStatus] = useState('not-started');
  const [workReport, setWorkReport] = useState('');
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'info' // 'info', 'success', 'error', 'warning'
  });
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPunchInImage, setCapturedPunchInImage] = useState(null);
  const [capturedPunchOutImage, setCapturedPunchOutImage] = useState(null);
  const [cameraAction, setCameraAction] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(false);
  const [tempReport, setTempReport] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [workReportSubmitted, setWorkReportSubmitted] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [employeeShift, setEmployeeShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [holidaysError, setHolidaysError] = useState(null);
  const [employees, setEmployees] = useState([]);  // Array of employees
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [userRole, setUserRole] = useState(null);  // User's role from token
  const [showAlert, setShowAlert] = useState(false);
  const [imageModal, setImageModal] = useState({ isOpen: false, imageUrl: '', title: '' });

  // Handle keyboard events for image modal (ESC key to close)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && imageModal.isOpen) {
        closeImageModal();
      }
    };

    if (imageModal.isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [imageModal.isOpen]);

  const videoRef = useRef(null);
  const streamRef = useRef(null);


  const formatTimeUTC = (date) => {
    if (!date) return '-';
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const time24 = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    return formatTo12Hour(time24);
  };

  const getActiveEmployeeId = () => {
    if (employee && employee._id) return employee._id;
    const selected = localStorage.getItem("selectedEmployeeId");
    if (selected) return selected;

    // Decode token fallback
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        return decoded.employeeId || decoded.id || decoded._id || decoded.userId;
      } catch (e) { }
    }
    return null;
  };


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (attendanceStatus === 'punched-in' && punchInTime) {
      const timer = setInterval(() => {
        const now = new Date();
        const diffMs = now - punchInTime;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        setWorkHours(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [attendanceStatus, punchInTime]);

  // Enhanced attendance fetching with consistent data processing
  const fetchAllAttendanceRecords = async () => {
    const empId = getActiveEmployeeId();
    if (!empId) {
      return [];
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      return [];
    }

    try {
      const response = await axios.get(
        `https://greenyellow-nightingale-241896.hostingersite.com/api/attendance`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 15000,
          validateStatus: (status) => status < 500
        }
      );

      // Handle authentication errors
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        setNotification({
          show: true,
          message: 'Session expired. Please log in again.',
          type: 'error',
          autoHide: true
        });
        return [];
      }

      if (response.status === 403) {
        setNotification({
          show: true,
          message: 'Access denied. Please check your permissions.',
          type: 'error',
          autoHide: true
        });
        return [];
      }

      // Handle different response formats
      let attendanceRecords = [];
      if (Array.isArray(response.data)) {
        attendanceRecords = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        attendanceRecords = response.data.data;
      } else if (response.data?.records && Array.isArray(response.data.records)) {
        attendanceRecords = response.data.records;
      } else if (response.data?.data) {
        attendanceRecords = [response.data.data];
      } else if (response.data) {
        attendanceRecords = [response.data];
      }

      // Get company ID for filtering safely
      let companyId = null;
      if (employee && employee.company) {
        companyId = typeof employee.company === 'object' ?
          (employee.company._id || employee.company.id) :
          employee.company;
      }

      if (!companyId) {
        try {
          const decoded = jwtDecode(token);
          companyId = decoded.companyId || decoded.company?._id || decoded.id;
        } catch (err) {
          // Continue without company filter if token decode fails
        }
      }

      // Filter and process records for current employee and company
      const processedRecords = attendanceRecords
        .filter(record => {
          if (!record) return false;

          // Match employee ID (handle both string and object references)
          const employeeMatch = record.employee === empId ||
            record.employee?._id === empId ||
            record.employee?.id === empId;

          // Match company ID (handle both string and object references)
          const companyMatch = !companyId ||
            record.company === companyId ||
            record.company?._id === companyId ||
            record.company?.id === companyId;

          return employeeMatch && companyMatch;
        })
        .map(record => {
          // Detect if this is an app entry (coordinates) vs manual entry
          const isAppEntry = (record.inLocation &&
            record.inLocation !== 'Manual Entry' &&
            record.inLocation !== 'Office' &&
            record.inLocation !== '-' &&
            /[0-9]/.test(record.inLocation)) ||
            (record.outLocation &&
              record.outLocation !== 'Manual Entry' &&
              record.outLocation !== 'Office' &&
              record.outLocation !== '-' &&
              /[0-9]/.test(record.outLocation));

          const inTime = record.inTime ? new Date(record.inTime) : null;
          const outTime = record.outTime ? new Date(record.outTime) : null;

          // Determine status - preserve manual statuses like Leave, Holiday, Work From Home
          let status = record.status || 'Present';

          // Standardize status casing
          if (status.toLowerCase() === 'absent') status = 'Absent';
          if (status.toLowerCase() === 'present') status = 'Present';
          if (status.toLowerCase() === 'incomplete') status = 'Incomplete';
          if (status.toLowerCase() === 'half-day' || status.toLowerCase() === 'half day') status = 'Half-day';

          // Only auto-calculate status if it's a default status (Present/Absent/Incomplete)
          // or if no status is explicitly set
          const manualStatuses = ['Leave', 'Holiday', 'Work From Home', 'Half Day', 'Half-day'];
          const shouldRecalculateStatus = !record.status || !manualStatuses.includes(status);

          if (shouldRecalculateStatus) {
            if (!inTime) {
              status = 'Absent';
            } else if (inTime && !outTime) {
              status = 'Incomplete';
            } else if (inTime && outTime) {
              // Calculate work hours to determine if it's a half day
              // Use workingTime if available (it's in minutes)
              const workMinutes = record.workingTime || (outTime - inTime) / (1000 * 60);
              if (workMinutes > 0 && workMinutes < 240) { // Less than 4 hours
                status = 'Half-day';
              } else if (workMinutes <= 0 && inTime && outTime) {
                // Fallback if workingTime is 0 but times exist
                status = 'Present';
              } else {
                status = 'Present';
              }
            }
          }

          // Parse date properly
          let recordDate = null;
          if (record.date) {
            try {
              recordDate = new Date(record.date).toISOString().split('T')[0];
            } catch (e) {
              recordDate = inTime ? inTime.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
            }
          } else if (inTime) {
            recordDate = inTime.toISOString().split('T')[0];
          } else {
            recordDate = new Date().toISOString().split('T')[0];
          }

          // Format times based on entry type
          const getFormattedTime24 = (dateObj, isApp) => {
            if (!dateObj) return null;
            if (isApp) {
              const hours = dateObj.getUTCHours().toString().padStart(2, '0');
              const minutes = dateObj.getUTCMinutes().toString().padStart(2, '0');
              return `${hours}:${minutes}`;
            }
            return dateObj.toTimeString().substring(0, 5);
          };

          const punchInTime24 = getFormattedTime24(inTime, isAppEntry);
          const punchOutTime24 = getFormattedTime24(outTime, isAppEntry);

          return {
            _id: record._id,
            date: recordDate,
            status: status,
            punchIn: punchInTime24 ? formatTo12Hour(punchInTime24) : '-',
            punchOut: punchOutTime24 ? formatTo12Hour(punchOutTime24) : '-',

            hours: inTime && outTime ? calculateWorkHours(
              punchInTime24,
              punchOutTime24
            ) : (inTime ? 'Ongoing' : '--:--'),
            punchInImage: record.inPhoto || null,
            punchOutImage: record.outPhoto || null,
            workingTime: record.workingTime || 0,
            inLocation: record.inLocation || 'Office',
            outLocation: record.outLocation || 'Office',
            report: record.workReport || record.report || '',
            // Additional metadata
            rawData: record // Keep original data for debugging
          };
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending

      return processedRecords;

    } catch (error) {
      // Enhanced error handling
      if (error.code === 'ECONNABORTED') {
        setNotification({
          show: true,
          message: 'Request timeout. Please check your internet connection.',
          type: 'error',
          autoHide: true
        });
      } else if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        let errorMessage = 'Failed to load attendance data.';
        switch (status) {
          case 401:
            localStorage.removeItem('authToken');
            errorMessage = 'Session expired. Please log in again.';
            break;
          case 403:
            errorMessage = 'Access denied. Please check your permissions.';
            break;
          case 404:
            errorMessage = 'Attendance service not found. Please contact support.';
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a moment and try again.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data?.message || `Server error (${status}). Please try again.`;
        }

        setNotification({
          show: true,
          message: errorMessage,
          type: 'error',
          autoHide: true
        });
      } else if (error.request) {
        setNotification({
          show: true,
          message: 'Network error. Please check your internet connection.',
          type: 'error',
          autoHide: true
        });
      }

      return [];
    }
  };

  // Fetch attendance history using the enhanced function
  const fetchAttendanceHistory = async () => {
    const records = await fetchAllAttendanceRecords();
    setAttendanceHistory(records);
  };

  useEffect(() => {
    fetchAttendanceHistory();
  }, [employee?._id]); // Fetch when employee data is available

  useEffect(() => {
    const filtered = attendanceHistory.filter(record => {
      const recordDate = new Date(record.date);
      return recordDate.getFullYear() === selectedYear &&
        recordDate.getMonth() === selectedMonth;
    });
    setFilteredHistory(filtered);
  }, [attendanceHistory, selectedYear, selectedMonth]);

  useEffect(() => {
    if (showCamera) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [showCamera]);



  useEffect(() => {
    const fetchCompany = async () => {
      // Wait for employee data to be loaded first
      if (!employee || !employee.company) {
        return;
      }

      const token = localStorage.getItem("authToken") || localStorage.getItem("token");

      if (!token) {
        const errorMsg = "No access token found. Please log in.";
        setError(errorMsg);
        setLoading(false);
        return;
      }

      // Extract company ID safely
      const companyId = typeof employee.company === 'object' ?
        (employee.company._id || employee.company.id) :
        employee.company;

      if (!companyId || companyId === '[object Object]') {
        console.error("Invalid company ID:", employee.company);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `https://greenyellow-nightingale-241896.hostingersite.com/api/companies/${companyId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );


        if (response.data) {
          const companyData = response.data.data || response.data;
          setSelectedCompany(companyData);
        } else {
          throw new Error("Empty company response received.");
        }
      } catch (err) {
        console.warn("Failed to fetch company data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [employee?._id]); // Depend on employee data


  useEffect(() => {
    const fetchEmployeeOrCompany = async () => {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");

      if (!token) {
        const errorMsg = "No access token found. Please log in.";
        setError(errorMsg);
        setLoading(false);
        return;
      }

      let decoded = null;
      try {
        decoded = jwtDecode(token);
        setUserRole(decoded.role);  // Set the role (e.g., 'employee' or 'company')
      } catch (decodeErr) {
        console.error("Token decoding failed:", decodeErr);
        setError("Invalid authentication token. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        if (decoded.role === 'company' || decoded.role === 'manager' || decoded.role === 'Manager') {
          console.log("decoded", decoded);

          // If the user is a company, fetch the list of employees
          const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || decoded.id;
          if (!companyId) {
            throw new Error("Company ID not found in token.");
          }

          const employeesResponse = await axios.get(
            `https://greenyellow-nightingale-241896.hostingersite.com/api/employees/company/${companyId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (employeesResponse.data) {
            const employeesData = employeesResponse.data.data || employeesResponse.data;
            setEmployees(employeesData);

            const savedEmpId = localStorage.getItem("selectedEmployeeId");
            const savedEmp = savedEmpId ? employeesData.find(emp => emp._id === savedEmpId) : null;

            if (savedEmp) {
              setEmployee(savedEmp);
              setSelectedEmployeeId(savedEmp._id);
            } else if (employeesData.length > 0) {
              setEmployee(employeesData[0]);
              setSelectedEmployeeId(employeesData[0]._id);
              localStorage.setItem("selectedEmployeeId", employeesData[0]._id);
            }
            setLoading(false);
          } else {
            throw new Error("No employees found for this company.");
          }
        } else {
          // For ALL other roles (employee, manager, admin, etc.)
          const employeeId = decoded.employeeId || decoded.id || decoded._id || decoded.userId;
          if (!employeeId) {
            throw new Error("Employee ID not found in token.");
          }

          try {
            const response = await axios.get(
              `https://greenyellow-nightingale-241896.hostingersite.com/api/employees/${employeeId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (response.data) {
              const employeeData = response.data.data || response.data;
              setEmployee(employeeData);
              setError(null);
            } else {
              throw new Error("Empty response data received");
            }
          } catch (fetchErr) {
            console.warn("Failed to fetch employee profile, using fallback from token", fetchErr);
            // Construct a fallback employee object from JWT claims so the dashboard continues to work
            const fallbackEmployee = {
              _id: employeeId,
              teamMemberName: decoded.name || decoded.username || 'Employee',
              company: decoded.companyId || decoded.company || null,
              email: decoded.email || ''
            };
            setEmployee(fallbackEmployee);
            setError(null);
          }
        }
      } catch (err) {
        console.error('Detailed Error in fetchEmployeeOrCompany:', {
          message: err.message,
          status: err.response?.status,
          url: err.config?.url,
        });
        setError(`Error: ${err.message}. Check console for details.`);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeOrCompany();
  }, []);


  useEffect(() => {
    if (employee) {
      fetchEmployeeShift();
    }
  }, [employee?._id]);

  // Fetch holidays when company data is available
  useEffect(() => {
    if (selectedCompany && selectedCompany._id) {
      fetchHolidays();
    }
  }, [selectedCompany?._id]);



  // Modify fetchEmployeeShift to fetch from API
  const fetchEmployeeShift = async () => {
    if (!employee || !employee._id) {
      setEmployeeShift(null); // Reset to avoid stale data
      return;
    }

    try {
      // Check if shift is embedded in employee (fallback for existing data)
      if (employee.shift && typeof employee.shift === 'object' && employee.shift.startTime) {
        const shiftInfo = {
          shiftName: employee.shift.name || 'Standard Shift',
          startTime: employee.shift.startTime,
          endTime: employee.shift.endTime,
          workingHours: employee.shift.workingHours || 8,
          breakTime: employee.shift.breakTime || 60,
          shiftType: employee.shift.type || 'Regular'
        };
        setEmployeeShift(shiftInfo);
        return;
      }

      // If shift is an ID or not embedded, fetch it from the API
      const shiftId = typeof employee.shift === 'object' ?
        (employee.shift._id || employee.shift.id) :
        (employee.shift || employee.shiftId); // Adjust based on your API
      if (!shiftId) {
        console.warn('No shift ID found for employee:', employee._id);
        setEmployeeShift({
          shiftName: 'Standard Shift',
          startTime: '09:00',
          endTime: '17:00',
          workingHours: 8,
          breakTime: 60,
          shiftType: 'Regular'
        });
        return;
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error('No auth token');
      }

      const response = await axios.get(
        `https://greenyellow-nightingale-241896.hostingersite.com/api/shifts/${shiftId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );

      const shiftData = response.data.data || response.data;
      const shiftInfo = {
        shiftName: shiftData.name || 'Standard Shift',
        startTime: shiftData.startTime || '09:00',
        endTime: shiftData.endTime || '17:00',
        workingHours: shiftData.workingHours || 8,
        breakTime: shiftData.breakTime || 60,
        shiftType: shiftData.type || 'Regular'
      };

      setEmployeeShift(shiftInfo);
    } catch (error) {
      console.error('Error fetching shift:', error);
      // Fallback to defaults
      setEmployeeShift({
        shiftName: 'Standard Shift',
        startTime: '09:00',
        endTime: '17:00',
        workingHours: 8,
        breakTime: 60,
        shiftType: 'Regular'
      });
      setNotification({
        show: true,
        message: 'Failed to load shift data. Using defaults.',
        type: 'warning'
      });
    }
  };

  // Ensure useEffect calls it correctly
  useEffect(() => {
    if (employee && employee._id) {
      fetchEmployeeShift();
    } else {
      setEmployeeShift(null); // Clear on no employee
    }
  }, [employee?._id]); // This should trigger on selection


  // 🎯 COMPREHENSIVE HOLIDAY FETCHING FUNCTIONALITY
  const fetchHolidays = async () => {
    // Only fetch if company data is available
    if (!selectedCompany || !selectedCompany._id) {
      console.log('Company data not available for holiday fetching');
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      console.log('🔑 No access token found for holiday fetching');
      setHolidaysError('No access token found. Please log in.');
      return;
    }

    setLoadingHolidays(true);
    setHolidaysError(null);

    try {
      console.log('🚀 FETCHING HOLIDAYS FOR COMPANY:', selectedCompany._id);

      // JWT token validation
      let decoded;
      try {
        decoded = jwtDecode(token);
        console.log('🔍 HOLIDAY FETCH TOKEN DECODED:', {
          id: decoded.id,
          role: decoded.role,
          companyId: decoded.companyId || 'Not in token',
          exp: new Date(decoded.exp * 1000).toLocaleString()
        });
      } catch (tokenError) {
        console.error('❌ Invalid token for holiday fetch:', tokenError);
        localStorage.removeItem('authToken');
        setHolidaysError('Invalid token. Please log in again.');
        return;
      }

      const response = await axios.get(
        `https://greenyellow-nightingale-241896.hostingersite.com/api/holidays/company/${selectedCompany._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 15000,
          validateStatus: (status) => status < 500
        }
      );

      console.log('📡 HOLIDAYS API RESPONSE:', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        dataType: typeof response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : 'Not array',
        data: response.data
      });

      // Handle authentication errors
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        setHolidaysError('Session expired. Please log in again.');
        setNotification({
          show: true,
          message: 'Session expired. Please log in again.',
          type: 'error',
          autoHide: true
        });
        return;
      }

      if (response.status === 403) {
        setHolidaysError('Access denied. Please check your permissions.');
        setNotification({
          show: true,
          message: 'Access denied to holiday data.',
          type: 'error',
          autoHide: true
        });
        return;
      }

      if (response.status === 404) {
        console.log('📅 No holidays found for company');
        setHolidays([]);
        setNotification({
          show: true,
          message: 'No holidays configured for your company.',
          type: 'info',
          autoHide: true
        });
        return;
      }

      // Handle different response formats
      let holidayRecords = [];
      if (Array.isArray(response.data)) {
        holidayRecords = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        holidayRecords = response.data.data;
      } else if (response.data?.holidays && Array.isArray(response.data.holidays)) {
        holidayRecords = response.data.holidays;
      } else if (response.data?.data) {
        holidayRecords = [response.data.data];
      } else if (response.data) {
        holidayRecords = [response.data];
      }

      console.log('🎯 PROCESSED HOLIDAYS:', {
        totalHolidays: holidayRecords.length,
        holidays: holidayRecords.map((holiday, index) => ({
          index: index + 1,
          id: holiday._id || holiday.id,
          name: holiday.name || holiday.holidayName || holiday.title,
          date: holiday.date || holiday.holidayDate,
          description: holiday.description || holiday.details,
          type: holiday.type || holiday.holidayType || 'General',
          isActive: holiday.isActive !== undefined ? holiday.isActive : true,
          company: holiday.company || 'Not specified',
          createdAt: holiday.createdAt,
          updatedAt: holiday.updatedAt
        }))
      });

      // Process and validate holiday data
      const processedHolidays = holidayRecords
        .filter(holiday => {
          // Basic validation
          if (!holiday) return false;

          // Must have a date
          const holidayDate = holiday.date || holiday.holidayDate;
          if (!holidayDate) {
            console.log('⚠️ Holiday missing date:', holiday);
            return false;
          }

          // Validate date format
          try {
            new Date(holidayDate);
            return true;
          } catch (e) {
            console.log('⚠️ Invalid holiday date format:', holidayDate);
            return false;
          }
        })
        .map(holiday => {
          const holidayDate = holiday.date || holiday.holidayDate;
          return {
            _id: holiday._id || holiday.id,
            name: holiday.name || holiday.holidayName || holiday.title || 'Holiday',
            date: new Date(holidayDate).toISOString().split('T')[0], // Format as YYYY-MM-DD
            description: holiday.description || holiday.details || '',
            type: holiday.type || holiday.holidayType || 'General',
            isActive: holiday.isActive !== undefined ? holiday.isActive : true,
            company: holiday.company,
            originalData: holiday // Keep for debugging
          };
        })
        .filter(holiday => holiday.isActive) // Only include active holidays
        .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date

      console.log('✅ FINAL PROCESSED HOLIDAYS:', {
        count: processedHolidays.length,
        holidays: processedHolidays
      });

      setHolidays(processedHolidays);

      // Show success notification
      if (processedHolidays.length > 0) {
        setNotification({
          show: true,
          message: `✅ Loaded ${processedHolidays.length} company holidays`,
          type: 'success',
          autoHide: true
        });
      }

    } catch (error) {
      console.error('❌ HOLIDAY FETCH ERROR:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout
        }
      });

      // Enhanced error handling
      if (error.code === 'ECONNABORTED') {
        setHolidaysError('Request timeout. Please check your internet connection.');
        setNotification({
          show: true,
          message: 'Holiday fetch timeout. Please try again.',
          type: 'error',
          autoHide: true
        });
      } else if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        let errorMessage = 'Failed to load company holidays.';
        switch (status) {
          case 401:
            localStorage.removeItem('authToken');
            errorMessage = 'Session expired. Please log in again.';
            break;
          case 403:
            errorMessage = 'Access denied to holiday data.';
            break;
          case 404:
            errorMessage = 'No holidays found for your company.';
            setHolidays([]); // Set empty array for 404
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a moment and try again.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data?.message || `Server error (${status}). Please try again.`;
        }

        setHolidaysError(errorMessage);
        setNotification({
          show: true,
          message: errorMessage,
          type: 'error',
          autoHide: true
        });
      } else if (error.request) {
        setHolidaysError('Network error. Please check your internet connection.');
        setNotification({
          show: true,
          message: 'Network error loading holidays.',
          type: 'error',
          autoHide: true
        });
      } else {
        setHolidaysError('Unexpected error loading holidays.');
        setNotification({
          show: true,
          message: 'Unexpected error loading holidays.',
          type: 'error',
          autoHide: true
        });
      }
    } finally {
      setLoadingHolidays(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      setShowAlert("Could not access camera. Please check permissions.");
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  // Function to compress image - single implementation
  const compressImage = (imageDataUrl, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = imageDataUrl;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxWidth) {
            width *= maxWidth / height;
            height = maxWidth;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to JPEG with specified quality
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      };

      img.onerror = () => {
        resolve(imageDataUrl); // Return original if compression fails
      };
    });
  };

  const handlePunchIn = async () => {
    setCameraAction('punchin');
    setShowCamera(true);
  };

  const handlePunchOut = async () => {
    if (!workReportSubmitted) {
      setShowAlert('Please submit your work report before punching out.');
      return;
    }
    setCameraAction('punchout');
    setShowCamera(true);
  };

  const handleSubmitReport = () => {
    if (workReport.trim() === '') {
      setShowAlert('Please provide a work report before submitting.');
      return;
    }

    setWorkReportSubmitted(true);
    setShowAlert('Work report submitted successfully! You may now punch out.');
  };

  const captureImage = async () => {
    if (!videoRef.current) {
      const error = 'Video reference not available';
      setNotification({
        show: true,
        message: 'Camera not available. Please ensure your camera is connected and permissions are granted.',
        type: 'error'
      });
      throw new Error(error);
    }

    try {

      // Capture the image
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;

      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Invalid video dimensions. Please ensure the camera is properly connected.');
      }

      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

      // Convert to data URL and compress
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);

      const compressedImage = await compressImage(imageDataUrl);

      const now = new Date();
      const today = now.toISOString().split('T')[0];

      // Get IDs from employee data and JWT token
      let employeeId, companyId;

      if (employee) {
        employeeId = employee._id;
        companyId = typeof employee.company === 'object' ?
          (employee.company._id || employee.company.id) :
          employee.company;
      } else {
        // Fallback to JWT token
        try {
          const token = localStorage.getItem("authToken");
          const decoded = jwtDecode(token);
          employeeId = decoded.employeeId || decoded.id || decoded._id || decoded.userId;
          companyId = decoded.companyId || decoded.company;
        } catch (err) {
        }
      }

      if (!employeeId || !companyId) {
        throw new Error('Missing employee or company information. Please ensure you are properly logged in and employee data is loaded.');
      }

      if (cameraAction === 'punchin') {

        // Create new attendance record with all required fields
        const attendanceData = {
          company: companyId,
          employee: employeeId,
          date: today,
          inTime: now.toISOString(),
          inLocation: 'Office',
          inPhoto: compressedImage,
          status: 'Present',
          workingTime: 0
        };


        try {
          const savedAttendance = await createAttendance(attendanceData);

          // Update state with the saved attendance
          setCurrentAttendance(savedAttendance);
          setPunchInTime(new Date(savedAttendance.inTime));
          setAttendanceStatus('punched-in');
          setCapturedPunchInImage(compressedImage);

          // Show success message
          setNotification({
            show: true,
            message: 'Punched in successfully!',
            type: 'success'
          });


        } catch (error) {

          setNotification({
            show: true,
            message: error.message || 'Failed to save punch in. Please try again.',
            type: 'error'
          });

          throw error;
        }

      } else if (cameraAction === 'punchout') {

        // Get existing attendance record
        let existingAttendance = currentAttendance;
        if (!existingAttendance) {
          try {
            existingAttendance = await fetchTodaysAttendance();
          } catch (fetchError) {
          }
        }

        if (!existingAttendance || !existingAttendance._id) {
          setNotification({
            show: true,
            message: 'No punch-in record found. Please punch in first.',
            type: 'error'
          });
          return null;
        }

        setCapturedPunchOutImage(compressedImage);

        // Calculate working time in minutes
        const punchInTime = existingAttendance.inTime ? new Date(existingAttendance.inTime) : new Date();
        const workingTimeMinutes = Math.round((now - punchInTime) / (1000 * 60));

        // Prepare update data for punch out
        const updateData = {
          outTime: now.toISOString(),
          outLocation: 'Office',
          outPhoto: compressedImage,
          status: 'Completed',
          workingTime: workingTimeMinutes
        };


        try {
          const savedAttendance = await updateAttendance(existingAttendance._id, updateData);

          // Update state with the saved attendance
          const updatedAttendance = { ...existingAttendance, ...savedAttendance };
          setCurrentAttendance(updatedAttendance);
          setPunchOutTime(new Date(updatedAttendance.outTime));
          setWorkHours(calculateWorkHours(
            new Date(updatedAttendance.inTime).toTimeString().substring(0, 5),
            new Date(updatedAttendance.outTime).toTimeString().substring(0, 5)
          ));
          setAttendanceStatus('completed');

          // Show success message
          setNotification({
            show: true,
            message: 'Punched out successfully!',
            type: 'success'
          });


        } catch (error) {
          setNotification({
            show: true,
            message: error.message || 'Failed to save punch out. Please try again.',
            type: 'error'
          });
          throw error;
        }
      }

      // Stop camera and close modal
      stopCamera();
      setShowCamera(false);

    } catch (error) {

      // Stop camera and close modal on error
      stopCamera();
      setShowCamera(false);

      // Show error notification if not already shown
      if (!error.message?.includes('Failed to')) {
        setNotification({
          show: true,
          message: `Failed to process attendance: ${error.message || 'Please try again.'}`,
          type: 'error'
        });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Present': return 'bg-green-100 text-green-800';
      case 'Late': return 'bg-yellow-100 text-yellow-800';
      case 'Absent': return 'bg-red-100 text-red-800';
      case 'Half-day': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDotColor = (status) => {
    switch (status) {
      case 'Present': return 'bg-green-500';
      case 'Late': return 'bg-yellow-500';
      case 'Absent': return 'bg-red-500';
      case 'Half-day':
      case 'Half Day': return 'bg-orange-500';
      case 'Incomplete': return 'bg-blue-500';
      case 'Leave': return 'bg-red-500';
      case 'Holiday': return 'bg-purple-500';
      case 'Work From Home': return 'bg-indigo-500';
      default: return 'bg-gray-500';
    }
  };

  // Validate attendance data before sending to API
  const validateAttendanceData = (data) => {
    const requiredFields = ['company', 'employee', 'date', 'inTime'];
    const missingFields = requiredFields.filter(field => !data[field]);

    if (missingFields.length > 0) {
      const error = new Error(`Missing required fields: ${missingFields.join(', ')}`);
      error.status = 400;
      throw error;
    }

    // Validate ObjectId format for company and employee
    if (data.company && !data.company.match(/^[0-9a-fA-F]{24}$/)) {
      throw new Error('Invalid company ID format');
    }

    if (data.employee && !data.employee.match(/^[0-9a-fA-F]{24}$/)) {
      throw new Error('Invalid employee ID format');
    }

    // Validate date format
    if (data.date && isNaN(new Date(data.date).getTime())) {
      throw new Error('Invalid date format');
    }

    return true;
  };

  const createAttendance = async (attendanceData) => {

    const token = localStorage.getItem("authToken");
    const employeeId = localStorage.getItem("selectedEmployeeId");
    const today = new Date().toISOString().split('T')[0];
    const API_BASE_URL = 'https://greenyellow-nightingale-241896.hostingersite.com/api/attendance';
    const LOCAL_STORAGE_KEY = `attendance_${employeeId}_${today}`;

    if (!token) {
      throw new Error('No authentication token found. Please log in.');
    }

    // Validate the data before sending
    validateAttendanceData(attendanceData);

    // Save to local storage first as backup
    const localAttendanceData = {
      ...attendanceData,
      _id: `local_${Date.now()}`, // Temporary local ID
      _isLocal: true,
      _createdAt: new Date().toISOString()
    };

    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(localAttendanceData));
    } catch (localError) {
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      timeout: 15000
    };

    try {
      const response = await axios.post(API_BASE_URL, attendanceData, config);

      // Update local storage with server response
      try {
        const serverData = { ...response.data, _synced: true, _syncedAt: new Date().toISOString() };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(serverData));
      } catch (localError) {
      }

      return response.data;
    } catch (error) {

      // For 500 errors, return the local data and show a warning
      if (error.response?.status === 500) {
        setNotification({
          show: true,
          message: 'Attendance saved locally. Will sync when server is available.',
          type: 'warning',
          autoHide: true
        });
        return localAttendanceData;
      }

      let errorMessage = 'Failed to create attendance record. ';
      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.message || 'Invalid attendance data provided.';
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message;
      }

      throw new Error(errorMessage);
    }
  };

  const updateAttendance = async (attendanceId, attendanceData) => {

    const token = localStorage.getItem("authToken");
    const API_BASE_URL = 'https://greenyellow-nightingale-241896.hostingersite.com/api/attendance';

    if (!token) {
      throw new Error('No authentication token found. Please log in.');
    }

    if (!attendanceId) {
      throw new Error('Attendance ID is required for updates.');
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      timeout: 15000
    };

    try {
      const response = await axios.put(`${API_BASE_URL}/${attendanceId}`, attendanceData, config);
      return response.data;
    } catch (error) {

      let errorMessage = 'Failed to update attendance record. ';
      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Attendance record not found.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.message || 'Invalid attendance data provided.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message;
      }

      throw new Error(errorMessage);
    }
  };

  // Fetch attendance by specific date using the centralized function
  const fetchAttendanceByDate = async (targetDate) => {
    console.log('Fetching attendance for date:', targetDate);

    if (!employee || !employee._id) {
      throw new Error('Employee information not available. Please refresh the page.');
    }

    try {
      // Use the centralized function to get all records
      const allRecords = await fetchAllAttendanceRecords();
      console.log("All attendance records fetched:", allRecords);

      // Find ALL records for the specific date
      const dateRecords = allRecords.filter(record => record.date === targetDate);

      if (dateRecords && dateRecords.length > 0) {
        return dateRecords;
      }

      // No record found for this date
      return null;

    } catch (error) {
      // Re-throw the error with context
      throw new Error(`Failed to fetch attendance for ${targetDate}: ${error.message}`);
    }
  };

  // Fetch today's attendance from the database
  const fetchTodaysAttendance = async () => {
    const token = localStorage.getItem("authToken");
    const today = new Date().toISOString().split('T')[0];
    const API_BASE_URL = 'https://greenyellow-nightingale-241896.hostingersite.com/api/attendance';

    if (!token) {
      throw new Error('No access token found');
    }

    // Get IDs from token and employee data
    let companyId, employeeId;
    try {
      const decoded = jwtDecode(token);

      employeeId = decoded.employeeId || decoded.id || decoded._id || decoded.userId;

      // Prioritize employee data for companyId since JWT doesn't contain it
      if (employee?.company) {
        companyId = typeof employee.company === 'object' ?
          (employee.company._id || employee.company.id) :
          employee.company;
      } else {
        // Fallback to token (though it likely won't have companyId)
        companyId = decoded.companyId ||
          decoded.company?._id ||
          decoded.company?.id ||
          decoded.company ||
          decoded.orgId ||
          decoded.organizationId;
      }

    } catch (err) {
      throw new Error('Invalid token format');
    }

    if (!employeeId) {
      throw new Error('Employee ID not found in token');
    }

    if (!companyId) {
    }

    const LOCAL_STORAGE_KEY = `attendance_${employeeId}_${today}`;

    // First check local storage
    try {
      const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (localData) {
        const parsedData = JSON.parse(localData);
        // If we have local data, try to sync with server in background
        if (parsedData._isLocal) {
        }
        return parsedData;
      }
    } catch (localError) {
    }

    const config = {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      timeout: 10000
    };


    try {
      // Get all attendance records and filter on client side for now
      // In production, you might want to add query parameters to the API
      const response = await axios.get(API_BASE_URL, config);


      // Handle different response formats
      let attendanceRecords = [];
      if (Array.isArray(response.data)) {
        attendanceRecords = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        attendanceRecords = response.data.data;
      } else if (response.data?.data) {
        attendanceRecords = [response.data.data];
      } else if (response.data) {
        attendanceRecords = [response.data];
      }

      // Filter for today's attendance for the current employee and company
      const todaysAttendance = attendanceRecords.find(record => {
        const recordDate = record.date ? new Date(record.date).toISOString().split('T')[0] : null;
        return recordDate === today &&
          record.employee === employeeId &&
          record.company === companyId;
      });

      // Cache the result in local storage
      if (todaysAttendance) {
        try {
          const cacheData = { ...todaysAttendance, _synced: true, _cachedAt: new Date().toISOString() };
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cacheData));
        } catch (cacheError) {
        }
      }

      return todaysAttendance || null;

    } catch (error) {

      // For 500 errors, try to use local storage as fallback
      if (error.response?.status === 500) {
        try {
          const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
          if (localData) {
            const parsedData = JSON.parse(localData);
            return parsedData;
          }
        } catch (localError) {
        }
        return null;
      }

      let errorMessage = 'Failed to load attendance data.';

      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'No attendance records found.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setNotification({
        show: true,
        message: errorMessage,
        type: 'error',
        autoHide: true
      });

      return null;
    }
  };

  // Helper function to check for locally stored attendance as fallback
  const checkLocalAttendance = (companyId, employeeId, date) => {
    try {
      // Get all attendance keys from local storage
      const keys = Object.keys(localStorage).filter(key => key.startsWith('attendance_'));

      // Try to find a matching attendance record
      for (const key of keys) {
        try {
          const localData = JSON.parse(localStorage.getItem(key));

          // Skip if data is not an object or doesn't have required fields
          if (!localData || typeof localData !== 'object') continue;

          // Parse the date from the record
          const recordDate = localData.date
            ? new Date(localData.date).toISOString().split('T')[0]
            : null;

          // Check if this record matches our criteria
          if (recordDate === date &&
            localData.company === companyId &&
            localData.employee === employeeId) {
            return localData;
          }
        } catch (parseError) {
          continue;
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const loadTodaysAttendance = async () => {
    // Only load if employee data is available
    if (!employee || !employee._id) {
      return;
    }

    try {
      const attendance = await fetchTodaysAttendance();

      if (!attendance) {
        // Initialize with default values
        setPunchInTime(null);
        setPunchOutTime(null);
        setWorkHours('00:00');
        setAttendanceStatus('not-started');
        setCurrentAttendance(null);
        return null;
      }

      // Set the current attendance record
      setCurrentAttendance(attendance);

      // Detect if this is an app entry (coordinates) vs manual entry
      const isAppEntry = attendance.inLocation &&
        attendance.inLocation !== 'Manual Entry' &&
        attendance.inLocation !== 'Office' &&
        attendance.inLocation !== '-' &&
        /[0-9]/.test(attendance.inLocation);

      // Ensure we have valid date objects
      const inTime = attendance.inTime ? new Date(attendance.inTime) : null;
      const outTime = attendance.outTime ? new Date(attendance.outTime) : null;

      // Validate dates
      if (inTime && isNaN(inTime.getTime())) {
        throw new Error('Invalid punch-in time format');
      }
      if (outTime && isNaN(outTime.getTime())) {
        throw new Error('Invalid punch-out time format');
      }

      setPunchInTime(inTime);

      // Set captured images if available
      if (attendance.inPhoto) {
        setCapturedPunchInImage(attendance.inPhoto);
      }
      if (attendance.outPhoto) {
        setCapturedPunchOutImage(attendance.outPhoto);
      }

      if (outTime) {
        // If already punched out
        setPunchOutTime(outTime);
        // Convert working time from minutes to HH:MM format
        let workingMinutes = attendance.workingTime || 0;

        // If workingTime is missing but we have times, calculate it
        if (!workingMinutes && inTime && outTime) {
          workingMinutes = Math.round((outTime - inTime) / (1000 * 60));
        }

        const hours = Math.floor(workingMinutes / 60);
        const minutes = workingMinutes % 60;
        setWorkHours(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
        setAttendanceStatus('completed');
      } else if (inTime) {
        // If only punched in
        setAttendanceStatus('punched-in');

        // Calculate current work hours
        const now = new Date();
        // If app entry, we might need to adjust 'now' or 'inTime' for the calculation
        // but for 'Ongoing' display, local calculation is usually fine as long as inTime is local
        const diffMs = now - inTime;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        setWorkHours(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
      } else {
        // No valid times
        setAttendanceStatus('not-started');
      }
    } catch (error) {
      // Reset to default state on error
      setPunchInTime(null);
      setPunchOutTime(null);
      setWorkHours('00:00');
      setAttendanceStatus('not-started');
      setCurrentAttendance(null);
    }
  };

  useEffect(() => {
    if (employee) {
      fetchAttendanceHistory();
      loadTodaysAttendance();
    }
  }, [employee, selectedEmployeeId]);  // Add selectedEmployeeId as dependency


  // Auto-hide notifications after 5 seconds
  useEffect(() => {
    if (notification.show && notification.autoHide !== false) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  const viewImage = (imageSrc, title = 'Image Preview') => {
    if (!imageSrc) return;
    setImageModal({
      isOpen: true,
      imageUrl: imageSrc,
      title: title
    });
  };

  const closeImageModal = () => {
    setImageModal({ isOpen: false, imageUrl: '', title: '' });
  };

  // Helper function to process raw attendance records into chronologically valid Punch In -> Punch Out pairs
  const processPunchEvents = (records) => {
    const events = [];

    records.forEach(r => {
      const rawData = r.rawData || r;

      // Look for any nested arrays that might contain punch history
      const nestedArrays = [
        rawData.punches, rawData.history, rawData.punchHistory,
        rawData.sessions, rawData.events, rawData.logs
      ];

      let foundNestedEvents = false;

      for (const arr of nestedArrays) {
        if (Array.isArray(arr) && arr.length > 0) {
          foundNestedEvents = true;
          arr.forEach(item => {
            // Handle { type: 'in', time: '...' } format
            if (item.type && item.time) {
              events.push({
                type: item.type === 'in' || item.type === 'punch-in' ? 'in' : 'out',
                time: new Date(item.time),
                original: item
              });
            }
            // Handle { inTime: '...', outTime: '...' } format
            else {
              const inT = item.inTime || item.punchIn;
              const outT = item.outTime || item.punchOut;
              if (inT) events.push({ type: 'in', time: new Date(inT), original: item });
              if (outT) events.push({ type: 'out', time: new Date(outT), original: item });
            }
          });
          break; // Stop looking at other array names if we found one
        }
      }

      // Fallback: If no nested array exists, use the top-level properties
      if (!foundNestedEvents) {
        const rawInTime = rawData.inTime || rawData.punchIn;
        const rawOutTime = rawData.outTime || rawData.punchOut;

        if (rawInTime) {
          events.push({ type: 'in', time: new Date(rawInTime), original: rawData });
        }
        if (rawOutTime) {
          events.push({ type: 'out', time: new Date(rawOutTime), original: rawData });
        }
      }
    });

    // Sort events chronologically
    events.sort((a, b) => a.time.getTime() - b.time.getTime());

    const sessions = [];
    let currentIn = null;

    events.forEach(event => {
      if (event.type === 'in') {
        if (currentIn) {
          // Open session -> Push previous as incomplete before starting new one
          sessions.push({ inEvent: currentIn, outEvent: null });
        }
        currentIn = event;
      } else if (event.type === 'out') {
        if (currentIn) {
          // Valid pair
          sessions.push({ inEvent: currentIn, outEvent: event });
          currentIn = null;
        } else {
          // If we receive an out event without an in event, it could be a mismatched pair
          // from a backend that overwrote the inTime but left the outTime.
          // We will store it as a session with only an outEvent so it is not lost.
          sessions.push({ inEvent: null, outEvent: event });
        }
      }
    });

    if (currentIn) {
      sessions.push({ inEvent: currentIn, outEvent: null });
    }

    // Map into standard UI display records
    const pairedRecords = sessions.map(session => {
      const inOrig = session.inEvent?.original;
      const outOrig = session.outEvent?.original;

      const isAppEntry = (inOrig?.inLocation &&
        inOrig.inLocation !== 'Manual Entry' &&
        inOrig.inLocation !== 'Office' &&
        inOrig.inLocation !== '-' &&
        /[0-9]/.test(inOrig.inLocation)) ||
        (outOrig?.outLocation &&
          outOrig.outLocation !== 'Manual Entry' &&
          outOrig.outLocation !== 'Office' &&
          outOrig.outLocation !== '-' &&
          /[0-9]/.test(outOrig.outLocation)) ||
        (inOrig?.rawData?.inLocation &&
          inOrig.rawData.inLocation !== 'Manual Entry' &&
          inOrig.rawData.inLocation !== 'Office' &&
          inOrig.rawData.inLocation !== '-' &&
          /[0-9]/.test(inOrig.rawData.inLocation));

      const getLocalTimeString = (dateObj, isApp) => {
        if (!dateObj) return '-';
        if (isApp) {
          const hours = dateObj.getUTCHours().toString().padStart(2, '0');
          const minutes = dateObj.getUTCMinutes().toString().padStart(2, '0');
          return formatTo12Hour(`${hours}:${minutes}`);
        }
        return formatTo12Hour(dateObj.toTimeString().substring(0, 5));
      };

      const punchInStr = session.inEvent ? getLocalTimeString(session.inEvent.time, isAppEntry) : '-';
      const punchOutStr = session.outEvent ? getLocalTimeString(session.outEvent.time, isAppEntry) : '-';

      let hours = '--:--';
      if (session.inEvent && session.outEvent) {
        const diffMs = session.outEvent.time - session.inEvent.time;
        const mins = Math.floor(diffMs / (1000 * 60));
        if (mins >= 0) {
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          hours = `${h}:${m.toString().padStart(2, '0')}`;
        } else {
          hours = '0:00';
        }
      } else if (session.inEvent) {
        hours = 'Ongoing';
      }

      return {
        _id: inOrig?._id || outOrig?._id || Math.random().toString(),
        punchIn: punchInStr,
        punchOut: punchOutStr,
        hours: hours,
        punchInImage: session.inEvent ? (inOrig?.inPhoto || inOrig?.punchInImage) : null,
        punchOutImage: session.outEvent ? (outOrig?.outPhoto || outOrig?.punchOutImage) : null,
        inLocation: session.inEvent ? (inOrig?.inLocation) : null,
        outLocation: session.outEvent ? (outOrig?.outLocation) : null,
        report: session.outEvent ? (outOrig?.workReport || outOrig?.report || '') : (session.inEvent ? (inOrig?.workReport || inOrig?.report || '') : ''),
        status: session.outEvent ? 'Present' : 'Incomplete',
        date: session.inEvent ? inOrig?.date : (session.outEvent ? outOrig?.date : null)
      };
    });

    return pairedRecords;
  };

  const openAttendanceDetails = async (date, record) => {
    setSelectedDate(date);

    // 🎯 CHECK FOR COMPANY HOLIDAY FIRST
    const holidayForDate = holidays.find(holiday => holiday.date === date);
    const isHolidayDate = !!holidayForDate;

    console.log('📅 OPENING ATTENDANCE DETAILS:', {
      date: date,
      isHolidayDate: isHolidayDate,
      holidayInfo: holidayForDate,
      hasExistingRecord: !!record
    });

    if (isHolidayDate) {
      // 🎉 HOLIDAY DATE - Show holiday information instead of attendance
      console.log('🎉 DISPLAYING HOLIDAY DETAILS:', holidayForDate);

      setSelectedRecord({
        date: date,
        isHoliday: true,
        holidayInfo: holidayForDate,
        status: 'Holiday',
        loading: false,
        error: false,
        noData: false
      });
      setTempReport('');
      setIsModalOpen(true);
      return;
    }

    // Always show loading state first for non-holiday dates
    setSelectedRecord({
      loading: true,
      date: date,
      status: 'Loading...',
      punchIn: '-',
      punchOut: '-',
      hours: '--:--',
      report: '',
      isHoliday: false
    });
    setIsModalOpen(true);

    try {
      // Always fetch fresh data from server for accurate information
      const fetchedRecords = await fetchAttendanceByDate(date);
      console.log("fetchedRecords", fetchedRecords);

      if (fetchedRecords && fetchedRecords.length > 0) {
        // Process the raw records into chronological sessions
        const processedSessions = processPunchEvents(fetchedRecords);

        const primaryRecord = processedSessions.length > 0 ? processedSessions[processedSessions.length - 1] : fetchedRecords[0];

        // Found attendance record
        setSelectedRecord({
          ...primaryRecord,
          allPunchRecords: processedSessions,
          loading: false,
          error: false,
          noData: false,
          isHoliday: false
        });
        setTempReport(primaryRecord.report || '');

        // Update selected record without changing the calendar's attendanceHistory color styling
        // (Removing the setAttendanceHistory call to prevent auto-changing cell colors when clicked)
      } else {
        // No attendance record found for this date
        const todayStr = new Date().toISOString().split('T')[0];
        if (date === todayStr) {
          const empId = getActiveEmployeeId();
          let localRecord = null;
          if (empId) {
            try {
              const cached = localStorage.getItem(`attendance_${empId}_${todayStr}`);
              if (cached) {
                localRecord = JSON.parse(cached);
              }
            } catch (e) {
              console.error("Failed to read local cache:", e);
            }
          }

          // Prioritize localStorage values, then fall back to component state/context, then defaults
          const pInTime = localRecord?.inTime || localRecord?.punchIn || punchInTime;
          const pOutTime = localRecord?.outTime || localRecord?.punchOut || punchOutTime;
          const pInImage = localRecord?.inPhoto || localRecord?.punchInImage || capturedPunchInImage;
          const pOutImage = localRecord?.outPhoto || localRecord?.punchOutImage || capturedPunchOutImage;
          const pInLoc = localRecord?.inLocation || currentAttendance?.inLocation || 'Office';
          const pOutLoc = localRecord?.outLocation || currentAttendance?.outLocation || 'Office';

          let resolvedStatus = localRecord?.status || 'Absent';
          if (resolvedStatus === 'Absent' || !localRecord) {
            if (attendanceStatus === 'completed') {
              const [hours] = workHours.split(':').map(Number);
              resolvedStatus = hours < 4 ? 'Half-day' : 'Present';
            } else if (attendanceStatus === 'punched-in' || pInTime) {
              resolvedStatus = 'Incomplete';
            }
          }

          const formatTime = (time) => {
            if (!time) return '-';
            try {
              const d = time instanceof Date ? time : new Date(time);
              if (isNaN(d.getTime())) {
                if (typeof time === 'string' && (time.includes('AM') || time.includes('PM'))) {
                  return time;
                }
                if (typeof time === 'string' && time.includes(':')) {
                  return formatTo12Hour(time);
                }
                return '-';
              }
              const time24 = d.toTimeString().substring(0, 5);
              return formatTo12Hour(time24);
            } catch (e) {
              return '-';
            }
          };

          const resolvedHours = localRecord?.workingTime || localRecord?.hours || workHours || '00:00';

          setSelectedRecord({
            date: date,
            status: resolvedStatus,
            punchIn: formatTime(pInTime),
            punchOut: formatTime(pOutTime),
            hours: typeof resolvedHours === 'number' ? `${Math.floor(resolvedHours / 60)}:${(resolvedHours % 60).toString().padStart(2, '0')}` : resolvedHours,
            report: localRecord?.workReport || localRecord?.report || workReport || '',
            punchInImage: pInImage,
            punchOutImage: pOutImage,
            inLocation: pInLoc,
            outLocation: pOutLoc,
            loading: false,
            error: false,
            noData: false,
            isHoliday: false
          });
          setTempReport(localRecord?.workReport || localRecord?.report || workReport || '');
        } else {
          setSelectedRecord({
            date: date,
            status: 'Absent',
            punchIn: '-',
            punchOut: '-',
            hours: '--:--',
            report: '',
            loading: false,
            error: false,
            noData: true,
            isHoliday: false
          });
          setTempReport('');
        }
      }
    } catch (error) {
      // Error occurred while fetching
      setSelectedRecord({
        date: date,
        status: 'Error',
        punchIn: '-',
        punchOut: '-',
        hours: '--:--',
        report: '',
        loading: false,
        error: true,
        noData: false,
        isHoliday: false,
        errorMessage: error.message || 'Failed to load attendance data'
      });
      setTempReport('');

      // Show error notification
      setNotification({
        show: true,
        message: `Failed to load attendance for ${new Date(date).toLocaleDateString()}: ${error.message}`,
        type: 'error',
        autoHide: true
      });
    }
  };

  const saveReport = () => {
    if (!selectedRecord) return;

    const updatedHistory = attendanceHistory.map(record => {
      if (record.date === selectedRecord.date) {
        return { ...record, report: tempReport };
      }
      return record;
    });

    setAttendanceHistory(updatedHistory);
    setEditingReport(false);
    setSelectedRecord(prev => ({ ...prev, report: tempReport }));
  };

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const generateCalendar = () => {
    const firstDay = new Date(selectedYear, selectedMonth, 1);
    const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.

    const today = new Date();
    const isCurrentMonth = today.getFullYear() === selectedYear && today.getMonth() === selectedMonth;

    // Get employee joining date
    let joiningDate = null;
    if (employee && employee.dateOfJoining) {
      joiningDate = new Date(employee.dateOfJoining);
    } else if (employee && employee.joiningDate) {
      joiningDate = new Date(employee.joiningDate);
    } else if (employee && employee.joinDate) {
      joiningDate = new Date(employee.joinDate);
    }

    // Get company weekly holidays
    let weeklyHolidays = [];
    if (selectedCompany && selectedCompany.weeklyHoliday) {
      weeklyHolidays = Array.isArray(selectedCompany.weeklyHoliday)
        ? selectedCompany.weeklyHoliday
        : [selectedCompany.weeklyHoliday];
    } else if (selectedCompany && selectedCompany.weeklyHolidays) {
      weeklyHolidays = Array.isArray(selectedCompany.weeklyHolidays)
        ? selectedCompany.weeklyHolidays
        : [selectedCompany.weeklyHolidays];
    }

    // Convert day names to day numbers (0 = Sunday, 1 = Monday, etc.)
    const dayNameToNumber = {
      'sunday': 0, 'sun': 0,
      'monday': 1, 'mon': 1,
      'tuesday': 2, 'tue': 2, 'tues': 2,
      'wednesday': 3, 'wed': 3,
      'thursday': 4, 'thu': 4, 'thur': 4, 'thurs': 4,
      'friday': 5, 'fri': 5,
      'saturday': 6, 'sat': 6
    };

    const holidayDayNumbers = weeklyHolidays.map(day =>
      dayNameToNumber[day.toLowerCase()] !== undefined ? dayNameToNumber[day.toLowerCase()] : -1
    ).filter(num => num !== -1);

    // 🎯 HOLIDAY INTEGRATION: Create holiday lookup for current month
    const monthHolidays = holidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate.getFullYear() === selectedYear && holidayDate.getMonth() === selectedMonth;
    });

    console.log('📅 HOLIDAYS FOR CURRENT MONTH:', {
      selectedMonth: selectedMonth + 1,
      selectedYear: selectedYear,
      totalHolidays: holidays.length,
      monthHolidays: monthHolidays.length,
      holidays: monthHolidays.map(h => ({ name: h.name, date: h.date, type: h.type }))
    });

    const calendar = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      calendar.push({ day: null, data: null, isEmpty: true });
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${(selectedMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const isToday = isCurrentMonth && day === today.getDate();
      const isPastDate = new Date(dateStr) < today && !isToday;

      // Check if this day is a weekly holiday
      const currentDate = new Date(dateStr);
      const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const isWeeklyHoliday = holidayDayNumbers.includes(dayOfWeek);

      // 🎯 CHECK FOR COMPANY HOLIDAYS
      const holidayForThisDate = monthHolidays.find(holiday => holiday.date === dateStr);
      const isCompanyHoliday = !!holidayForThisDate;

      // Find attendance record for this date from the centralized attendance history
      const attendanceRecord = attendanceHistory.find(record => record.date === dateStr);

      if (isToday && !attendanceRecord) {
        // For today, show current status even if no historical record
        let status = 'Absent';
        if (attendanceStatus === 'completed') {
          // Determine status based on work hours
          const [hours] = workHours.split(':').map(Number);
          status = hours < 4 ? 'Half-day' : 'Present';
        } else if (attendanceStatus === 'punched-in') {
          status = 'Incomplete';
        }

        const formatTime = (time) => {
          if (!time) return '-';
          try {
            const date = time instanceof Date ? time : new Date(time);
            if (isNaN(date.getTime())) return '-';
            // Always convert UTC to local time
            const time24 = date.toTimeString().substring(0, 5);
            return formatTo12Hour(time24);
          } catch (e) {
            return '-';
          }
        };

        const todayStatus = {
          date: dateStr,
          status: status,
          punchIn: formatTime(punchInTime),
          punchOut: formatTime(punchOutTime),
          hours: workHours,
          report: workReport,
          punchInImage: capturedPunchInImage,
          punchOutImage: capturedPunchOutImage,
          inLocation: currentAttendance?.inLocation || 'Office',
          outLocation: currentAttendance?.outLocation || 'Office'
        };
        calendar.push({ day, data: todayStatus, isToday: true, isPastDate: false, isWeeklyHoliday, isCompanyHoliday, holidayInfo: holidayForThisDate });
      } else if (!attendanceRecord && isPastDate) {
        // Check if this date is after joining date
        const currentDate = new Date(dateStr);
        const isAfterJoining = !joiningDate || currentDate >= joiningDate;

        if (isAfterJoining && !isWeeklyHoliday && !isCompanyHoliday) {
          // For past dates without attendance records (after joining, excluding weekly holidays and company holidays), mark as absent
          const absentRecord = {
            date: dateStr,
            status: 'Absent',
            punchIn: '-',
            punchOut: '-',
            hours: '--:--',
            report: '',
            punchInImage: null,
            punchOutImage: null,
            inLocation: '-',
            outLocation: '-'
          };
          calendar.push({ day, data: absentRecord, isToday: false, isPastDate: true, isAbsent: true, isWeeklyHoliday, isCompanyHoliday, holidayInfo: holidayForThisDate });
        } else {
          // For dates before joining, weekly holidays, or company holidays, show as normal calendar day without absence marking
          calendar.push({ day, data: null, isToday: false, isPastDate: true, isAbsent: false, isBeforeJoining: !isAfterJoining, isWeeklyHoliday, isCompanyHoliday, holidayInfo: holidayForThisDate });
        }
      } else {
        calendar.push({ day, data: attendanceRecord, isToday: isToday, isPastDate: isPastDate, isAbsent: false, isWeeklyHoliday, isCompanyHoliday, holidayInfo: holidayForThisDate });
      }
    }

    return calendar;
  };

  const calendarDays = useMemo(() => {
    console.log('Generating calendar...');  // This will only log once per memoization
    return generateCalendar();
  }, [selectedYear, selectedMonth, holidays, attendanceHistory, attendanceStatus, punchInTime, punchOutTime, workReport, employee]);  // Add dependencies as needed

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate monthly summary statistics
  const calculateMonthlySummary = () => {
    const currentMonthRecords = filteredHistory;
    const today = new Date();
    const currentMonth = selectedMonth;
    const currentYear = selectedYear;

    // Get total days in the selected month
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    let totalWorkingDays = 0;
    let totalHalfDays = 0;
    let totalLeaves = 0;
    let totalPresent = 0;
    let totalLate = 0;
    let totalIncomplete = 0;
    let totalFullDayWorking = 0; // New counter for full day working

    // Count attendance records
    currentMonthRecords.forEach(record => {
      // Calculate if it's a full day work (8+ hours)
      let isFullDay = false;
      if (record.hours && record.hours !== '--:--' && record.hours !== 'Ongoing') {
        const [hours, minutes] = record.hours.split(':').map(Number);
        const totalHours = hours + (minutes / 60);
        isFullDay = totalHours >= 8; // Consider 8+ hours as full day
      }

      switch (record.status) {
        case 'Present':
          totalPresent++;
          totalWorkingDays++;
          if (isFullDay) totalFullDayWorking++;
          break;
        case 'Late':
          totalLate++;
          totalWorkingDays++;
          if (isFullDay) totalFullDayWorking++;
          break;
        case 'Half-day':
          totalHalfDays++;
          totalWorkingDays++;
          // Half-day typically won't be full day, but check anyway
          if (isFullDay) totalFullDayWorking++;
          break;
        case 'Absent':
          totalLeaves++;
          break;
        case 'Incomplete':
          totalIncomplete++;
          totalWorkingDays++;
          if (isFullDay) totalFullDayWorking++;
          break;
        default:
          break;
      }
    });

    // Count absent days (past dates without attendance records, but only after dateOfJoining and excluding weekly holidays)
    let joiningDate = null;
    if (employee && employee.dateOfJoining) {
      joiningDate = new Date(employee.dateOfJoining);
    } else if (employee && employee.joiningDate) {
      joiningDate = new Date(employee.joiningDate);
    } else if (employee && employee.joinDate) {
      joiningDate = new Date(employee.joinDate);
    }

    // Get company weekly holidays for summary calculation
    let weeklyHolidays = [];
    if (selectedCompany && selectedCompany.weeklyHoliday) {
      weeklyHolidays = Array.isArray(selectedCompany.weeklyHoliday)
        ? selectedCompany.weeklyHoliday
        : [selectedCompany.weeklyHoliday];
    } else if (selectedCompany && selectedCompany.weeklyHolidays) {
      weeklyHolidays = Array.isArray(selectedCompany.weeklyHolidays)
        ? selectedCompany.weeklyHolidays
        : [selectedCompany.weeklyHolidays];
    }

    // Convert day names to day numbers for summary calculation
    const dayNameToNumber = {
      'sunday': 0, 'sun': 0,
      'monday': 1, 'mon': 1,
      'tuesday': 2, 'tue': 2, 'tues': 2,
      'wednesday': 3, 'wed': 3,
      'thursday': 4, 'thu': 4, 'thur': 4, 'thurs': 4,
      'friday': 5, 'fri': 5,
      'saturday': 6, 'sat': 6
    };

    const holidayDayNumbers = weeklyHolidays.map(day =>
      dayNameToNumber[day.toLowerCase()] !== undefined ? dayNameToNumber[day.toLowerCase()] : -1
    ).filter(num => num !== -1);

    // 🎯 HOLIDAY INTEGRATION: Get holidays for summary calculation
    const summaryMonthHolidays = holidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate.getFullYear() === currentYear && holidayDate.getMonth() === currentMonth;
    });

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const dateObj = new Date(dateStr);
      const isPastDate = dateObj < today;
      const isToday = dateObj.toDateString() === today.toDateString();

      // Check if this day is a weekly holiday
      const dayOfWeek = dateObj.getDay();
      const isWeeklyHoliday = holidayDayNumbers.includes(dayOfWeek);

      // Check if this day is a company holiday
      const isCompanyHoliday = summaryMonthHolidays.some(holiday => holiday.date === dateStr);

      // Only count past dates (not future dates or today) and only after joining date, excluding weekly holidays and company holidays
      const isAfterJoining = !joiningDate || dateObj >= joiningDate;

      if (isPastDate && !isToday && isAfterJoining && !isWeeklyHoliday && !isCompanyHoliday) {
        const hasRecord = currentMonthRecords.some(record => record.date === dateStr);
        if (!hasRecord) {
          totalLeaves++; // Count as absent/leave day (excluding weekly holidays and company holidays)
        }
      }
    }

    return {
      totalWorkingDays,
      totalHalfDays,
      totalLeaves,
      totalPresent,
      totalLate,
      totalIncomplete,
      totalFullDayWorking,
      totalCompanyHolidays: summaryMonthHolidays.length, // 🎉 Add holiday count
      totalRecords: currentMonthRecords.length,
      totalDaysInMonth: daysInMonth
    };
  };

  const monthlySummary = calculateMonthlySummary();

  const renderWorkHours = () => {
    if (attendanceStatus === 'not-started') {
      return '00:00';
    }

    if (attendanceStatus === 'completed' && punchInTime && punchOutTime) {
      // Calculate final work hours if not already done
      if (workHours === '00:00') {
        const diffMs = punchOutTime - punchInTime;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
      return workHours;
    }

    if (punchInTime) {
      return workHours; // This will be updated by the effect
    }

    return '00:00';
  };

  const mobileStyles = `
    @media (max-width: 768px) {
      .da-container {
        padding: 0 !important;
        overflow-x: hidden !important;
        width: 100% !important;
        box-sizing: border-box !important;
      }
      .da-card-main {
        background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%) !important;
        border: none !important;
        box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.5) !important;
        padding: 12px 10px !important;
        border-radius: 0 !important;
        margin-bottom: 12px !important;
        color: white !important;
      }
      .da-card-secondary {
        border-radius: 0 !important;
        border-left: none !important;
        border-right: none !important;
        padding: 12px 10px !important;
        margin-bottom: 12px !important;
      }
      .da-card-main h1, .da-card-main h2, .da-card-main h3, .da-card-main h4, .da-card-main p, .da-card-main span, .da-card-main div, .da-card-main label {
        color: white !important;
      }
      .da-card-main .da-glass-btn {
        background: rgba(255, 255, 255, 0.15) !important;
        backdrop-filter: blur(10px) !important;
        -webkit-backdrop-filter: blur(10px) !important;
        border: 1px solid rgba(255, 255, 255, 0.3) !important;
        color: white !important;
        border-radius: 12px !important;
        padding: 14px 20px !important;
        font-weight: 600 !important;
        font-size: 14px !important;
        transition: all 0.3s ease !important;
        text-align: center !important;
        width: 100% !important;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        margin: 8px 0 !important;
      }
      .da-card-main .da-glass-btn:active {
        background: rgba(255, 255, 255, 0.25) !important;
        box-shadow: 0 0 15px rgba(255, 255, 255, 0.4) !important;
        transform: translateY(-2px) !important;
      }
      .da-card-main .mcs-trigger, 
      .da-card-main .mcs-label, 
      .da-card-main .desktop-select,
      .da-card-main input {
        color: #1f2937 !important;
      }
      .da-card-main div.mcs-dropdown div {
        color: #334155 !important;
      }
      .da-card-main div.mcs-trigger {
        background: white !important;
      }
      .da-card-main label {
        color: white !important;
      }
      .da-card-main svg {
        color: white !important;
      }
      .da-card-main .bg-white {
        background: rgba(255, 255, 255, 0.1) !important;
      }
      .da-card-main .bg-gradient-to-r.from-indigo-600.to-purple-600.bg-clip-text.text-transparent {
        background: none !important;
        -webkit-text-fill-color: white !important;
        color: white !important;
      }
      /* specific inner card styling */
      .da-card-main .da-inner-card {
        background: rgba(255, 255, 255, 0.1) !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
        border-radius: 12px !important;
        padding: 6px !important;
      }
      /* hide specific gradient backgrounds */
      .da-card-main .bg-gradient-to-br, .da-card-main .bg-gradient-to-r {
        background: rgba(255, 255, 255, 0.1) !important;
      }
      /* ensure calendar text is readable */
      .da-card-main .text-gray-500 {
        color: rgba(255, 255, 255, 0.8) !important;
      }
      .da-card-main .border-gray-200 {
        border-color: rgba(255, 255, 255, 0.2) !important;
      }
      .desktop-select {
        display: none !important;
      }
      .mobile-custom-select {
        display: block !important;
      }
      .mcs-trigger {
        padding: 6px 10px !important;
        font-size: 12px !important;
        border-radius: 6px !important;
      }
      .mcs-option {
        padding: 8px 10px !important;
        font-size: 12px !important;
      }
      .mcs-dropdown {
        max-height: 180px !important;
        border-radius: 6px !important;
      }
    }
  `;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 md:py-6 md:px-4 lg:px-4 da-container">
      <style>{mobileStyles}</style>
      <div className="w-full md:max-w-[98%] mx-auto">
        <header className="mb-4 md:mb-8">
          {/* Enhanced Header with Better UI */}
          <div className="bg-white rounded-none md:rounded-2xl shadow-lg p-4 md:p-8 border-b md:border border-gray-100 da-card-main da-input-override">
            <div className="text-center">
              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                  <span className="text-indigo-600 font-medium">Loading employee data...</span>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-red-700 font-medium">{error}</span>
                  </div>
                </div>
              )}


              {/* Main Title */}
              <div className="mb-3">
                <h1 className="text-lg sm:text-2xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-1">
                  Daily Attendance System
                </h1>
                <p className="text-gray-500 sm:text-gray-600 text-xs sm:text-lg">Track your work hours and submit daily reports</p>
              </div>

              {(userRole === 'company' || userRole === 'manager' || userRole === 'Manager') && employees.length > 0 && (
                <div className="mb-4">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Select Employee:
                  </label>
                  <ResponsiveSelect
                    value={selectedEmployeeId || ''}
                    onChange={(e) => {
                      setSelectedEmployeeId(e.target.value);
                      const selectedEmp = employees.find(emp => emp._id === e.target.value);
                      if (selectedEmp) {
                        setEmployee(selectedEmp);
                      }
                    }}
                    placeholder="Select an employee"
                    options={employees.filter(emp => emp.isActive !== false).map(emp => ({
                      value: emp._id,
                      label: emp.teamMemberName || 'Unnamed Employee'
                    }))}
                  />
                </div>
              )}


              {/* Date, Time, Team Member & Shift Display */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-2 sm:p-4 border border-indigo-100 da-inner-card">
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 text-center">
                  {/* Date */}
                  <div className="bg-white rounded-lg p-1.5 sm:p-3 shadow-sm">
                    <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-0.5">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      <span className="text-[9px] sm:text-xs font-medium text-gray-600">Today</span>
                    </div>
                    <div className="text-[10px] sm:text-sm font-semibold text-gray-800">
                      {currentTime.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                  </div>

                  {/* Current Time */}
                  <div className="bg-white rounded-lg p-1.5 sm:p-3 shadow-sm">
                    <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-0.5">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      <span className="text-[9px] sm:text-xs font-medium text-gray-600">Time</span>
                    </div>
                    <div className="text-[10px] sm:text-sm font-bold text-indigo-600">
                      {currentTime.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>

                  {/* Team Member Name */}
                  <div className="bg-white rounded-lg p-1.5 sm:p-3 shadow-sm">
                    <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-0.5">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                      </svg>
                      <span className="text-[9px] sm:text-xs font-medium text-gray-600">Team Member</span>
                    </div>
                    <div className="text-[10px] sm:text-sm font-bold text-green-600 truncate max-w-full block">
                      {employee ? (
                        employee.teamMemberName ||
                        employee.fullName ||
                        employee.name ||
                        ((employee.firstName || '') + ' ' + (employee.lastName || '')).trim() ||
                        'Loading...'
                      ) : 'Loading...'}
                    </div>
                  </div>

                  {/* Shift Information */}
                  <div className="bg-white rounded-lg p-1.5 sm:p-3 shadow-sm">
                    <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-0.5">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      <span className="text-[9px] sm:text-xs font-medium text-gray-600">Shift</span>
                    </div>
                    <div className="text-[10px] sm:text-sm font-bold text-purple-600">
                      {employeeShift ? (
                        formatTo12Hour(employeeShift.startTime) + ' - ' + formatTo12Hour(employeeShift.endTime)
                      ) : (
                        employee ? 'Loading...' : '--:-- - --:--'
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Monthly Summary Cards */}
        <div className="mb-4 md:mb-8">
          <div className="bg-white md:rounded-2xl shadow-lg p-4 md:p-6 border-y md:border border-gray-100 da-card-secondary">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 sm:mb-6">
              <h2 className="text-sm sm:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Monthly Summary - {months[selectedMonth]} {selectedYear}
              </h2>
              <div className="text-[10px] sm:text-sm text-gray-500">
                Total Records: {monthlySummary.totalRecords}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-6">
              {/* Total Working Days Card */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                {/* Mobile View Card (Horizontal, Sleek, Compact) */}
                <div className="sm:hidden flex items-center justify-between w-full p-2 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="p-1.5 bg-green-100 rounded-lg flex-shrink-0">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-[11px] font-bold text-green-800 truncate">Total Working Days</h3>
                      <p className="text-[9px] text-green-600 font-medium truncate">
                        Present: {monthlySummary.totalPresent} • Late: {monthlySummary.totalLate}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 flex items-baseline gap-0.5">
                    <span className="text-base font-extrabold text-green-700">{monthlySummary.totalWorkingDays}</span>
                    <span className="text-[9px] text-green-600 font-medium">Days</span>
                  </div>
                </div>

                {/* Desktop View Card (Vertical, Standard) */}
                <div className="hidden sm:block p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-green-700">
                        {monthlySummary.totalWorkingDays}
                      </div>
                      <div className="text-sm text-green-600 font-medium">
                        Days
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-green-800">Total Working Days</h3>
                    <p className="text-sm text-green-600">
                      Includes Present ({monthlySummary.totalPresent}), Late ({monthlySummary.totalLate}), and Incomplete ({monthlySummary.totalIncomplete}) days
                    </p>
                    <div className="flex items-center justify-between text-xs text-green-700 bg-green-100 rounded-lg px-3 py-2">
                      <span>Present: {monthlySummary.totalPresent}</span>
                      <span>Late: {monthlySummary.totalLate}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Half Days Card */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                {/* Mobile View Card */}
                <div className="sm:hidden flex items-center justify-between w-full p-2 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="p-1.5 bg-orange-100 rounded-lg flex-shrink-0">
                      <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-[11px] font-bold text-orange-800 truncate">Total Half Days</h3>
                      <p className="text-[9px] text-orange-600 font-medium truncate">Work &lt; 4 hours</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 flex items-baseline gap-0.5">
                    <span className="text-base font-extrabold text-orange-700">{monthlySummary.totalHalfDays}</span>
                    <span className="text-[9px] text-orange-600 font-medium">Days</span>
                  </div>
                </div>

                {/* Desktop View Card */}
                <div className="hidden sm:block p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-orange-700">
                        {monthlySummary.totalHalfDays}
                      </div>
                      <div className="text-sm text-orange-600 font-medium">
                        Days
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-orange-800">Total Half Days</h3>
                    <p className="text-sm text-orange-600">
                      Days with less than 4 hours of work
                    </p>
                    <div className="flex items-center justify-center text-xs text-orange-700 bg-orange-100 rounded-lg px-3 py-2">
                      <span>Work Hours &lt; 4 hours</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Leaves Card */}
              <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                {/* Mobile View Card */}
                <div className="sm:hidden flex items-center justify-between w-full p-2 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="p-1.5 bg-red-100 rounded-lg flex-shrink-0">
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-[11px] font-bold text-red-800 truncate">Total Leaves</h3>
                      <p className="text-[9px] text-red-600 font-medium truncate">Excludes Holidays</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 flex items-baseline gap-0.5">
                    <span className="text-base font-extrabold text-red-700">{monthlySummary.totalLeaves}</span>
                    <span className="text-[9px] text-red-600 font-medium">Days</span>
                  </div>
                </div>

                {/* Desktop View Card */}
                <div className="hidden sm:block p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-red-100 rounded-lg">
                      <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-red-700">
                        {monthlySummary.totalLeaves}
                      </div>
                      <div className="text-sm text-red-600 font-medium">
                        Days
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-red-800">Total Leaves</h3>
                    <p className="text-sm text-red-600">
                      Days marked as absent (excluding company holidays)
                    </p>
                    <div className="flex items-center justify-center text-xs text-red-700 bg-red-100 rounded-lg px-3 py-2">
                      <span>Excludes Holidays</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Full Day Working Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                {/* Mobile View Card */}
                <div className="sm:hidden flex items-center justify-between w-full p-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
                      <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-[11px] font-bold text-blue-800 truncate">Full Day Working</h3>
                      <p className="text-[9px] text-blue-600 font-medium truncate">Work Hours &ge; 8 hours</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 flex items-baseline gap-0.5">
                    <span className="text-base font-extrabold text-blue-700">{monthlySummary.totalFullDayWorking}</span>
                    <span className="text-[9px] text-blue-600 font-medium">Days</span>
                  </div>
                </div>

                {/* Desktop View Card */}
                <div className="hidden sm:block p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-700">
                        {monthlySummary.totalFullDayWorking}
                      </div>
                      <div className="text-sm text-blue-600 font-medium">
                        Days
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-blue-800">Full Day Working</h3>
                    <p className="text-sm text-green-600">
                      Days with 8+ hours of work completed
                    </p>
                    <div className="flex items-center justify-center text-xs text-blue-700 bg-blue-100 rounded-lg px-3 py-2">
                      <span>Work Hours &ge; 8 hours</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 🎉 Company Holidays Card */}
              <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 col-span-1 sm:col-span-2 md:col-span-1 lg:col-span-1">
                {/* Mobile View Card */}
                <div className="sm:hidden flex items-center justify-between w-full p-2 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="p-1.5 bg-purple-100 rounded-lg flex-shrink-0">
                      <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-[11px] font-bold text-purple-800 truncate">Company Holidays</h3>
                      <p className="text-[9px] text-purple-600 font-medium truncate">No Attendance Required</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 flex items-baseline gap-0.5">
                    <span className="text-base font-extrabold text-purple-700">{monthlySummary.totalCompanyHolidays}</span>
                    <span className="text-[9px] text-purple-600 font-medium">Days</span>
                  </div>
                </div>

                {/* Desktop View Card */}
                <div className="hidden sm:block p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-purple-700">
                        {monthlySummary.totalCompanyHolidays}
                      </div>
                      <div className="text-sm text-purple-600 font-medium">
                        Days
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-purple-800">Company Holidays</h3>
                    <p className="text-sm text-purple-600">
                      Official company holidays and festivals
                    </p>
                    <div className="flex items-center justify-center text-xs text-purple-700 bg-purple-100 rounded-lg px-3 py-2">
                      <span>🎉 No Attendance Required</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Statistics Row */}
            {/* <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">
                    {monthlySummary.totalRecords > 0 ? 
                      Math.round((monthlySummary.totalWorkingDays / monthlySummary.totalRecords) * 100) : 0}%
                  </div>
                  <div className="text-sm text-gray-600">Attendance Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {monthlySummary.totalPresent}
                  </div>
                  <div className="text-sm text-gray-600">On Time Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {monthlySummary.totalLate}
                  </div>
                  <div className="text-sm text-gray-600">Late Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {monthlySummary.totalIncomplete}
                  </div>
                  <div className="text-sm text-gray-600">Incomplete Days</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {monthlySummary.totalFullDayWorking}
                  </div>
                  <div className="text-sm text-gray-600">Full Day Working</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {monthlySummary.totalCompanyHolidays}
                  </div>
                  <div className="text-sm text-gray-600">Company Holidays</div>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        {showCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                {cameraAction === 'punchin' ? 'Punch In Verification' : 'Punch Out Verification'}
              </h2>
              <p className="text-gray-600 mb-4 text-center">Please position your face in the frame</p>

              <div className="relative bg-gray-200 rounded-lg overflow-hidden mb-4">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="border-2 border-white border-dashed rounded-full w-48 h-48"></div>
                </div>
              </div>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={captureImage}
                  className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Capture
                </button>
                <button
                  onClick={() => {
                    setShowCamera(false);
                    setCameraAction('');
                  }}
                  className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-center">
          {/* Attendance History Card */}
          <div className="bg-white md:rounded-xl shadow-lg p-4 md:p-6 w-full max-w-4xl da-input-override da-card-secondary">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                📅 Attendance History
              </h2>
              <p className="text-gray-600 mt-2">View your monthly attendance records and calendar</p>
            </div>

            <div className="mb-4 flex border-b border-gray-200">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-2 font-medium ${viewMode === 'calendar' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-indigo-600'}`}
              >
                Calendar View
              </button>

            </div>

            {/* Year and Month Selector */}
            <div className="mb-6 flex justify-center">
              <div className="grid grid-cols-2 gap-4 w-full max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">📅 Year</label>
                  <ResponsiveSelect
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    placeholder="Select Year"
                    options={years.map(year => ({ value: year, label: year.toString() }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">📅 Month</label>
                  <ResponsiveSelect
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    placeholder="Select Month"
                    options={months.map((month, index) => ({ value: index, label: month }))}
                  />
                </div>
              </div>
            </div>

            {viewMode === 'calendar' ? (
              <div className="w-full">
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map(day => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map(({ day, data, isToday, isPastDate, isAbsent, isEmpty, isBeforeJoining, isWeeklyHoliday, isCompanyHoliday, holidayInfo }, index) => (
                    <div
                      key={index}
                      className={`aspect-square sm:aspect-auto sm:min-h-12 p-0.5 sm:p-1 border rounded-md text-center transition-all duration-200 flex flex-col items-center justify-center ${day ? 'cursor-pointer hover:shadow-md' : 'bg-gray-50'
                        } ${
                        // Background colors based on status (Company holidays take priority)
                        isEmpty ? 'bg-gray-50' :
                          isCompanyHoliday ? 'bg-purple-200 hover:bg-purple-300 border-purple-400' :
                            isWeeklyHoliday ? 'bg-gray-350 hover:bg-gray-400 border-gray-550' :
                              isBeforeJoining ? 'bg-gray-150 hover:bg-gray-200 border-gray-350' :
                                isAbsent || (data && data.status === 'Absent') ? 'bg-red-200 hover:bg-red-300 border-red-400' :
                                  data && data.status === 'Present' ? 'bg-green-200 hover:bg-green-300 border-green-400' :
                                    data && data.status === 'Late' ? 'bg-yellow-200 hover:bg-yellow-300 border-yellow-400' :
                                      data && data.status === 'Half-day' ? 'bg-orange-200 hover:bg-orange-300 border-orange-400' :
                                        data && data.status === 'Incomplete' ? 'bg-blue-200 hover:bg-blue-300 border-blue-400' :
                                          'bg-white hover:bg-gray-50 border-gray-200'
                        } ${
                        // Border styles (Company holidays get special border)
                        isCompanyHoliday ? 'border-2 border-purple-500' :
                          isWeeklyHoliday ? 'border-2 border-gray-500' :
                            isBeforeJoining ? 'border border-gray-400' :
                              isAbsent || (data && data.status === 'Absent') ? 'border-2 border-red-500' :
                                data && data.status === 'Present' ? 'border-2 border-green-500' :
                                  data && data.status === 'Late' ? 'border-2 border-yellow-500' :
                                    data && data.status === 'Half-day' ? 'border-2 border-orange-500' :
                                      data && data.status === 'Incomplete' ? 'border-2 border-blue-500' :
                                        data ? 'border-2 border-gray-400' : 'border border-gray-200'
                        } ${
                        // Today's special styling
                        isToday ? 'ring-2 ring-indigo-600 ring-offset-1' : ''
                        }`}
                      onClick={() => {
                        if (day) {
                          const dateStr = `${selectedYear}-${(selectedMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                          openAttendanceDetails(dateStr, data);
                        }
                      }}
                    >
                      {day && (
                        <>
                          <div className={`text-xs sm:text-sm font-medium ${isToday ? 'text-indigo-700 font-bold' :
                            isCompanyHoliday ? 'text-purple-700 font-bold' :
                              isWeeklyHoliday ? 'text-red-600 font-semibold' :
                                isBeforeJoining ? 'text-gray-500' :
                                  isAbsent || (data && data.status === 'Absent') ? 'text-red-700 font-semibold' :
                                    data && data.status === 'Present' ? 'text-green-700' :
                                      data && data.status === 'Late' ? 'text-yellow-700' :
                                        data && (data.status === 'Half-day' || data.status === 'Half Day') ? 'text-orange-700' :
                                          data && data.status === 'Incomplete' ? 'text-blue-700' :
                                            data && data.status === 'Leave' ? 'text-red-700 font-semibold' :
                                              data && data.status === 'Holiday' ? 'text-purple-700 font-bold' :
                                                data && data.status === 'Work From Home' ? 'text-indigo-700' :
                                                  'text-gray-700'
                            }`}>
                            {day}
                            {isToday && <span className="hidden sm:block text-[9px] sm:text-xs text-indigo-500 font-normal">Today</span>}
                            {isCompanyHoliday && (
                              <span className="hidden sm:block text-[8px] sm:text-xs text-purple-600 font-bold leading-tight" title={holidayInfo?.name}>
                                🎉 {holidayInfo?.name?.substring(0, 5) || 'Holiday'}
                              </span>
                            )}
                            {isWeeklyHoliday && !isCompanyHoliday && (
                              <span className="hidden sm:block text-[8px] sm:text-xs text-red-500 font-medium">
                                Off
                              </span>
                            )}
                            {isBeforeJoining && !isCompanyHoliday && <span className="hidden sm:block text-[8px] sm:text-xs text-gray-400">Before</span>}
                            {isAbsent && !isCompanyHoliday && <span className="text-xs text-red-500 block font-medium"></span>}
                          </div>
                          {data && (
                            <div className="flex justify-center mt-0.5">
                              <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${getStatusDotColor(data.status)}`}></div>
                            </div>
                          )}
                          {!data && isPastDate && !isBeforeJoining && !isWeeklyHoliday && !isCompanyHoliday && (
                            <div className="flex justify-center mt-0.5">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-red-500"></div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {/* Enhanced Legend */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Calendar Legend</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 text-xs">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-green-700 font-medium">Present</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                      <span className="text-yellow-700 font-medium">Late</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                      <span className="text-red-700 font-medium">Absent/Leave</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                      <span className="text-orange-700 font-medium">Half-day</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                      <span className="text-blue-700 font-medium">Incomplete</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-indigo-500 mr-2"></div>
                      <span className="text-indigo-700 font-medium">Work From Home</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded border-2 border-indigo-500 mr-2"></div>
                      <span className="text-indigo-700 font-medium">Today</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded bg-gray-100 border border-gray-400 mr-2"></div>
                      <span className="text-gray-600 font-medium">Before Joining</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded bg-gray-200 border-2 border-gray-500 mr-2"></div>
                      <span className="text-red-600 font-medium">Weekly Holiday</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded bg-purple-100 border-2 border-purple-400 mr-2"></div>
                      <span className="text-purple-700 font-medium">Company Holiday</span>
                    </div>
                    {loadingHolidays && (
                      <div className="flex items-center col-span-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600 mr-2"></div>
                        <span className="text-purple-600 font-medium">Loading holidays...</span>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            ) : (
              <div className="overflow-y-auto max-h-96">
                {filteredHistory.length > 0 ? (
                  filteredHistory.map((record, index) => (
                    <div key={index} className="py-3 border-b border-gray-100">
                      <div className="flex justify-between items-center">
                        <div className="font-medium text-gray-700">{record.date}</div>
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(record.status)}`}>
                          {record.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                        <div>
                          <div className="text-gray-500">In</div>
                          <div className="font-medium">{record.punchIn}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Out</div>
                          <div className="font-medium">{record.punchOut}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Hours</div>
                          <div className="font-medium">{record.hours}</div>
                        </div>
                      </div>
                      {(record.punchInImage || record.punchOutImage) && (
                        <div className="mt-2 flex space-x-2">
                          {record.punchInImage && (
                            <div className="relative cursor-pointer" onClick={() => viewImage(record.punchInImage, 'Punch In Image')}>
                              <img
                                src={record.punchInImage}
                                alt="Punch in"
                                className="w-10 h-10 object-cover rounded border border-gray-300 hover:opacity-90 transition-opacity"
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center">In</div>
                            </div>
                          )}
                          {record.punchOutImage && (
                            <div className="relative cursor-pointer" onClick={() => viewImage(record.punchOutImage, 'Punch Out Image')}>
                              <img
                                src={record.punchOutImage}
                                alt="Punch out"
                                className="w-10 h-10 object-cover rounded border border-gray-300 hover:opacity-90 transition-opacity"
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center">Out</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No attendance records found for {months[selectedMonth]} {selectedYear}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification Component */}
      {error && (
        <div className="fixed top-4 right-4 z-50 bg-red-500 text-white p-4 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Error: {error}</span>
            <button onClick={() => setError(null)} className="ml-3">
              <FiX size={18} />
            </button>
          </div>
          <p className="text-xs mt-2">Details: Request failed with status 404. Check the console for more info. Try refreshing or logging in again.</p>
        </div>
      )}

      {/* Attendance Details Modal */}
      {isModalOpen && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-xl font-semibold ${selectedRecord.isHoliday ? 'text-purple-800' : 'text-gray-800'
                  }`}>
                  {selectedRecord.isHoliday ? '🎉 Holiday Details' : 'Attendance Details'}
                </h3>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingReport(false);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-medium">
                    {new Date(selectedDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>

                {/* 🎉 HOLIDAY CONTENT SECTION */}
                {selectedRecord.isHoliday && selectedRecord.holidayInfo && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                    {/* Holiday Header */}
                    <div className="flex items-center justify-center mb-3">
                      <div className="text-4xl mr-3">🎉</div>
                      <div className="text-center">
                        <h4 className="text-lg font-bold text-purple-800">
                          {selectedRecord.holidayInfo.name}
                        </h4>
                        <p className="text-sm text-purple-600">
                          Company Holiday
                        </p>
                      </div>
                    </div>

                    {/* Holiday Details */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-purple-200">
                        <span className="text-purple-700 font-medium">Holiday Type:</span>
                        <span className="text-purple-800 font-semibold">
                          {selectedRecord.holidayInfo.type || 'General'}
                        </span>
                      </div>

                      {selectedRecord.holidayInfo.description && (
                        <div className="py-2 border-b border-purple-200">
                          <span className="text-purple-700 font-medium block mb-1">Description:</span>
                          <p className="text-purple-800 text-sm leading-relaxed">
                            {selectedRecord.holidayInfo.description}
                          </p>
                        </div>
                      )}

                      <div className="flex justify-between items-center py-2">
                        <span className="text-purple-700 font-medium">Status:</span>
                        <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                          🎉 Holiday
                        </span>
                      </div>
                    </div>

                    {/* Holiday Message */}
                    <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 mt-4">
                      <div className="flex items-center">
                        <div className="text-purple-600 mr-2">ℹ️ï</div>
                        <div className="text-purple-800 text-sm">
                          <strong>No attendance required:</strong> This is a company holiday.
                          Employees are not expected to mark attendance on this date.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {selectedRecord.loading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <span className="ml-3 text-gray-600">Loading attendance data...</span>
                  </div>
                )}

                {/* Error State */}
                {selectedRecord.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="text-red-600 mr-2">⚠️</div>
                      <div className="flex-1">
                        <div className="text-red-800 font-medium">Error Loading Data</div>
                        <div className="text-red-600 text-sm mt-1">{selectedRecord.errorMessage}</div>
                        <button
                          onClick={() => openAttendanceDetails(selectedDate)}
                          className="mt-2 text-xs text-red-700 hover:text-red-900 underline"
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* No Data State */}
                {selectedRecord.noData && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-center">
                      <div className="text-gray-500 mr-2">ℹ️ï</div>
                      <div className="text-gray-600">No attendance record found for this date</div>
                    </div>
                  </div>
                )}

                {/* Normal State - Only show for non-holiday dates */}
                {!selectedRecord.loading && !selectedRecord.error && !selectedRecord.isHoliday && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`px-2 py-1 rounded-full text-sm ${selectedRecord.noData ? 'bg-gray-100 text-gray-600' : getStatusColor(selectedRecord.status)
                        }`}>
                        {selectedRecord.status}
                      </span>
                    </div>

                    {selectedRecord.allPunchRecords && selectedRecord.allPunchRecords.length > 0 ? (
                      <div className="space-y-3 max-h-64 overflow-y-auto pr-1 mt-4">
                        {/* Calculate Total Work Hours across all sessions */}
                        <div className="flex justify-between items-center bg-indigo-50 p-3 rounded-lg border border-indigo-100 mb-2">
                          <span className="text-indigo-800 font-medium">Total Work Hours:</span>
                          <span className="text-indigo-900 font-bold">
                            {(() => {
                              let totalMins = 0;
                              let hasOngoing = false;
                              selectedRecord.allPunchRecords.forEach(record => {
                                if (record.hours === 'Ongoing') {
                                  hasOngoing = true;
                                } else if (record.hours && record.hours !== '--:--') {
                                  const [h, m] = record.hours.split(':').map(Number);
                                  if (!isNaN(h) && !isNaN(m)) {
                                    totalMins += (h * 60) + m;
                                  }
                                }
                              });
                              if (totalMins === 0 && hasOngoing) return 'Ongoing';
                              if (totalMins === 0) return '--:--';

                              const hours = Math.floor(totalMins / 60);
                              const mins = totalMins % 60;
                              const formattedTotal = `${hours}:${mins.toString().padStart(2, '0')}`;
                              return hasOngoing ? `${formattedTotal} (+ Ongoing)` : formattedTotal;
                            })()}
                          </span>
                        </div>

                        {selectedRecord.allPunchRecords.map((record, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                            <div className="text-sm font-semibold text-gray-700 mb-2 border-b border-gray-200 pb-1 flex justify-between">
                              <span>Session {selectedRecord.allPunchRecords.length > 1 ? selectedRecord.allPunchRecords.length - index : ''}</span>
                              {record.punchIn && record.punchIn !== '-' && index === selectedRecord.allPunchRecords.length - 1 && (
                                <span className="text-xs font-normal">
                                  {(() => {
                                    const { isLate, lateTime } = calculateLateTime(record.punchIn);
                                    return isLate ? (
                                      <span className="text-yellow-600">+{lateTime} late</span>
                                    ) : (
                                      <span className="text-green-600">On Time</span>
                                    );
                                  })()}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-1">
                                <div className="text-xs text-gray-500">Punch In:</div>
                                <div className="font-medium text-sm">{record.punchIn || '--:--'}</div>
                                {record.punchInImage && (
                                  <button
                                    onClick={() => viewImage(record.punchInImage, 'Punch In Image')}
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    View Image
                                  </button>
                                )}
                              </div>
                              <div className="space-y-1">
                                <div className="text-xs text-gray-500">Punch Out:</div>
                                <div className="font-medium text-sm">{record.punchOut || '--:--'}</div>
                                {record.punchOutImage && (
                                  <button
                                    onClick={() => viewImage(record.punchOutImage, 'Punch Out Image')}
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    View Image
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between">
                              <span className="text-xs text-gray-500">Duration:</span>
                              <span className="text-sm font-medium">{record.hours || '--:--'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Fallback for when allPunchRecords is empty or undefined
                      <>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="space-y-2">
                            <div className="text-gray-600">Punch In:</div>
                            <div className="font-medium">{selectedRecord.punchIn || '--:--'}</div>
                            {selectedRecord.punchInImage && !selectedRecord.noData && (
                              <button
                                onClick={() => viewImage(selectedRecord.punchInImage, 'Punch In Image')}
                                className="text-sm text-blue-600 hover:underline"
                              >
                                View Punch In Image
                              </button>
                            )}
                          </div>
                          <div className="space-y-2">
                            <div className="text-gray-600">Punch Out:</div>
                            <div className="font-medium">{selectedRecord.punchOut || '--:--'}</div>
                            {selectedRecord.punchOutImage && !selectedRecord.noData && (
                              <button
                                onClick={() => viewImage(selectedRecord.punchOutImage, 'Punch Out Image')}
                                className="text-sm text-blue-600 hover:underline"
                              >
                                View Punch Out Image
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Add Work Hours Row */}
                        <div className="flex justify-between items-center py-2 border-t border-b border-gray-100 mt-4">
                          <span className="text-gray-600">Work Hours:</span>
                          <span className="font-medium">
                            {selectedRecord.hours || '--:--'}
                          </span>
                        </div>

                        {/* Add Late Time Row */}
                        {selectedRecord.punchIn && selectedRecord.punchIn !== '-' && !selectedRecord.noData && (
                          <div className="flex justify-between items-center py-2 border-t border-b border-gray-100">
                            <span className="text-gray-600">Late Time:</span>
                            <span className="font-medium">
                              {(() => {
                                const { isLate, lateTime } = calculateLateTime(selectedRecord.punchIn);
                                return isLate ? (
                                  <span className="text-yellow-600">+{lateTime} late</span>
                                ) : (
                                  <span className="text-green-600">On Time</span>
                                );
                              })()}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Image Preview Modal */}
      {imageModal.isOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm transition-opacity duration-200 p-4"
          onClick={closeImageModal}
        >
          <div
            className="relative max-w-3xl max-h-[90vh] mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <span>📷</span>
                {imageModal.title || 'Image Preview'}
              </h3>
              <button
                onClick={closeImageModal}
                className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all duration-200"
                aria-label="Close modal"
              >
                <FiX size={22} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 bg-gray-900 flex items-center justify-center min-h-[250px] max-h-[70vh] overflow-auto">
              <img
                src={imageModal.imageUrl}
                alt={imageModal.title || 'Image Preview'}
                className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
              />
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center text-sm">
              <span className="text-xs text-gray-500 hidden sm:inline">
                Click outside or press <kbd className="px-2 py-0.5 bg-white border border-gray-300 rounded text-xs shadow-sm">ESC</kbd> to close
              </span>
              <div className="flex space-x-3 ml-auto">
                <a
                  href={imageModal.imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-lg transition-colors font-medium text-xs sm:text-sm flex items-center shadow-sm"
                >
                  Open Original ↗
                </a>
                <button
                  onClick={closeImageModal}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium text-xs sm:text-sm shadow-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyAttendance;

